import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Cloud, Database, HardDrive, ListChecks, RefreshCw, X, XCircle } from 'lucide-react';
import { DataDownloadTask, tradingApi } from '../services/api';
import { formatDownloadTaskBarCount } from '../utils/dataDownloadTask.ts';

const activeStatuses = ['queued', 'running', 'cancelling'];

const statusClass = (status: DataDownloadTask['status']) => {
  if (status === 'completed') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
  if (status === 'failed') return 'text-red-400 bg-red-500/10 border-red-500/20';
  if (status === 'cancelled') return 'text-muted bg-white/5 border-white/10';
  if (status === 'cancelling') return 'text-amber-300 bg-amber-500/10 border-amber-500/20';
  return 'text-neon bg-neon/10 border-neon/20';
};

const storageClass = (status?: string) => {
  if (status === 'saved' || status === 'cached' || status === 'synced') return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20';
  if (status === 'failed') return 'text-amber-300 bg-amber-500/10 border-amber-500/20';
  if (status === 'skipped' || status === 'not_attempted') return 'text-muted bg-white/5 border-white/10';
  return 'text-neon bg-neon/10 border-neon/20';
};

const StorageBadges: React.FC<{ task: DataDownloadTask }> = ({ task }) => {
  const local = task.storage?.local;
  const lake = task.storage?.research_lake;
  return (
    <div className="flex flex-wrap gap-1.5">
      <span
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[9px] font-black uppercase tracking-widest ${storageClass(local?.status)}`}
        title={local?.message || 'Trader DB storage status'}
      >
        <HardDrive className="w-3 h-3" />
        DB {local?.status || 'pending'}
      </span>
      <span
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[9px] font-black uppercase tracking-widest ${storageClass(lake?.status)}`}
        title={lake?.message || 'Research Lake sync status'}
      >
        <Cloud className="w-3 h-3" />
        Lake {lake?.status || 'pending'}
      </span>
    </div>
  );
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TaskDrawer: React.FC<{
  onClose?: () => void;
  onTaskCountChange?: (count: number) => void;
}> = ({ onClose, onTaskCountChange }) => {
  const [tasks, setTasks] = useState<DataDownloadTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'ACTIVE' | 'DONE'>('ALL');

  const fetchTasks = useCallback(async () => {
    try {
      const nextTasks = await tradingApi.getDataDownloadTasks();
      setTasks(nextTasks);
      setError('');
      onTaskCountChange?.(nextTasks.filter((task) => activeStatuses.includes(task.status)).length);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [onTaskCountChange]);

  useEffect(() => {
    fetchTasks();
    const interval = window.setInterval(fetchTasks, 3000);
    return () => window.clearInterval(interval);
  }, [fetchTasks]);

  const filteredTasks = useMemo(() => {
    if (activeFilter === 'ACTIVE') return tasks.filter((task) => activeStatuses.includes(task.status));
    if (activeFilter === 'DONE') return tasks.filter((task) => !activeStatuses.includes(task.status));
    return tasks;
  }, [activeFilter, tasks]);

  const cancelTask = async (taskId: string) => {
    try {
      await tradingApi.cancelDataDownloadTask(taskId);
      await fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel task');
    }
  };

  const getFilterButton = (filter: typeof activeFilter, label: string) => (
    <button
      onClick={() => setActiveFilter(filter)}
      className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all duration-200 border ${activeFilter === filter
        ? 'bg-neon text-black border-neon shadow-[0_0_15px_rgba(92,246,205,0.4)]'
        : 'bg-white/5 text-muted border-white/10 hover:text-white hover:bg-white/10'
        }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col h-full bg-surface/95 backdrop-blur-2xl border-l border-white/10 shadow-[-20px_0_40px_rgba(0,0,0,0.5)]">
      <div className="flex items-center justify-between p-6 border-b border-white/5 bg-white/5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <ListChecks className="w-5 h-5 text-neon" />
            {tasks.some((task) => activeStatuses.includes(task.status)) && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-neon rounded-full animate-pulse" />
            )}
          </div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-white">Tasks</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchTasks}
            className="p-1.5 rounded-lg hover:bg-white/10 text-muted hover:text-white transition"
            title="Refresh tasks"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-muted hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-black/10 overflow-x-auto no-scrollbar">
        {getFilterButton('ALL', 'All')}
        {getFilterButton('ACTIVE', 'Running')}
        {getFilterButton('DONE', 'Done')}
      </div>

      {error && (
        <div className="mx-4 mt-4 border border-red-500/20 bg-red-500/10 text-red-300 px-3 py-2 rounded-xl text-xs">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
        {loading && tasks.length === 0 ? (
          <div className="text-center text-muted text-sm py-10 animate-pulse">Loading tasks...</div>
        ) : filteredTasks.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-muted gap-4 opacity-30 pb-20">
            <CheckCircle2 className="w-12 h-12" />
            <span className="text-sm font-medium tracking-wide">
              {activeFilter === 'ACTIVE' ? 'No running tasks' : 'No tasks found'}
            </span>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg bg-surface/50 border border-white/5 p-1.5">
                    <Database className="w-4 h-4 text-neon" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-white truncate">
                      {task.request.display_name || task.request.symbols?.join(', ') || task.request.symbol || '-'} {task.request.sec_type || 'STK'}{task.request.stk_type ? `/${task.request.stk_type}` : ''} {task.request.timeframe}
                    </p>
                    <p className="text-[10px] text-muted font-mono">
                      {task.request.data_source} {task.request.fetch_profile || 'balanced'} #{task.id}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${statusClass(task.status)}`}>
                  {task.status}
                </span>
              </div>

              <div className="space-y-1.5">
                <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${task.status === 'failed' ? 'bg-red-400' : task.status === 'completed' ? 'bg-emerald-400' : 'bg-neon'}`}
                    style={{ width: `${['completed', 'failed'].includes(task.status) ? 100 : task.progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted">
                  <span>{task.progress}%</span>
                  <span>{formatDateTime(task.updated_at)}</span>
                </div>
              </div>

              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <StorageBadges task={task} />
                  <p className="text-[11px] text-white/60 leading-relaxed">{task.message}</p>
                  <p className="text-[10px] text-muted font-mono mt-1">
                    {task.request.start_date} to {task.request.end_date}
                    {formatDownloadTaskBarCount(task.bars)
                      ? ` | ${formatDownloadTaskBarCount(task.bars)} bars`
                      : ''}
                  </p>
                </div>
                {task.can_cancel !== false && activeStatuses.includes(task.status) && (
                  <button
                    onClick={() => cancelTask(task.id)}
                    className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg shrink-0"
                    title="Cancel task"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskDrawer;
